import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { PricePoint, TradingSignal, PnFChart, CapeScore, FScore, PortfolioPosition } from '../types';
import config from '../config';

// Supabase configuration
const supabaseUrl = config.supabaseUrl;
const supabaseAnonKey = config.supabaseAnonKey;

// Initialize the Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Type definitions for database tables
export interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSettings {
  id: string;
  user_id: string;
  initial_cash: number;
  current_cash: number;
  created_at: string;
  updated_at: string;
}

export interface PerformanceSnapshot {
  id: string;
  user_id: string;
  date: string;
  portfolio_value: number;
  cash_balance: number;
  created_at: string;
}

// Supabase service with typed methods for our application
export const supabaseService = {
  // Auth methods
  auth: {
    signUp: async (email: string, password: string) => {
      return await supabase.auth.signUp({
        email,
        password,
      });
    },
    
    signIn: async (email: string, password: string) => {
      return await supabase.auth.signInWithPassword({
        email,
        password,
      });
    },
    
    signOut: async () => {
      return await supabase.auth.signOut();
    },
    
    getSession: async () => {
      return await supabase.auth.getSession();
    },
    
    getCurrentUser: async () => {
      return await supabase.auth.getUser();
    },
  },
  
  // Profile methods
  profile: {
    // Get the current user's profile
    getProfile: async (): Promise<Profile | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) {
          if (error.code === 'PGRST116') { // Record not found
            // Create a profile if it doesn't exist
            return await supabaseService.profile.createProfile(user.id, user.email || '');
          }
          throw error;
        }
        
        return data as Profile;
      } catch (error) {
        console.error('Error getting profile:', error);
        return null;
      }
    },
    
    // Create a new profile
    createProfile: async (userId: string, email: string): Promise<Profile | null> => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email,
            display_name: email.split('@')[0], // Simple display name from email
          })
          .select()
          .single();
          
        if (error) throw error;
        
        // Also create portfolio settings
        await supabaseService.portfolio.createInitialSettings(userId);
        
        return data as Profile;
      } catch (error) {
        console.error('Error creating profile:', error);
        return null;
      }
    },
    
    // Update the current user's profile
    updateProfile: async (profile: Partial<Profile>): Promise<Profile | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('profiles')
          .update(profile)
          .eq('id', user.id)
          .select()
          .single();
          
        if (error) throw error;
        
        return data as Profile;
      } catch (error) {
        console.error('Error updating profile:', error);
        return null;
      }
    },
  },
  
  // Portfolio methods
  portfolio: {
    // Create initial portfolio settings
    createInitialSettings: async (userId: string, initialCash: number = 100000): Promise<PortfolioSettings | null> => {
      try {
        const { data, error } = await supabase
          .from('portfolio_settings')
          .insert({
            user_id: userId,
            initial_cash: initialCash,
            current_cash: initialCash,
          })
          .select()
          .single();
          
        if (error) throw error;
        
        return data as PortfolioSettings;
      } catch (error) {
        console.error('Error creating portfolio settings:', error);
        return null;
      }
    },
    
    // Get portfolio settings
    getSettings: async (): Promise<PortfolioSettings | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('portfolio_settings')
          .select('*')
          .eq('user_id', user.id)
          .single();
          
        if (error) {
          if (error.code === 'PGRST116') { // Record not found
            // Create settings if they don't exist
            return await supabaseService.portfolio.createInitialSettings(user.id);
          }
          throw error;
        }
        
        return data as PortfolioSettings;
      } catch (error) {
        console.error('Error getting portfolio settings:', error);
        return null;
      }
    },
    
    // Update portfolio settings
    updateSettings: async (settings: Partial<PortfolioSettings>): Promise<PortfolioSettings | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get current settings first
        const currentSettings = await supabaseService.portfolio.getSettings();
        
        if (!currentSettings) {
          return await supabaseService.portfolio.createInitialSettings(
            user.id, 
            settings.initial_cash || 100000
          );
        }
        
        const { data, error } = await supabase
          .from('portfolio_settings')
          .update(settings)
          .eq('id', currentSettings.id)
          .select()
          .single();
          
        if (error) throw error;
        
        return data as PortfolioSettings;
      } catch (error) {
        console.error('Error updating portfolio settings:', error);
        return null;
      }
    },
    
    // Get positions
    getPositions: async (activeOnly: boolean = true): Promise<PortfolioPosition[]> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return [];
        
        let query = supabase
          .from('positions')
          .select('*')
          .eq('user_id', user.id);
          
        if (activeOnly) {
          query = query.eq('is_active', true);
        }
        
        const { data, error } = await query.order('created_at', { ascending: false });
          
        if (error) throw error;
        
        return data as PortfolioPosition[];
      } catch (error) {
        console.error('Error getting positions:', error);
        return [];
      }
    },
    
    // Create position
    createPosition: async (position: Omit<PortfolioPosition, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_active'>): Promise<PortfolioPosition | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get settings to check cash balance
        const settings = await supabaseService.portfolio.getSettings();
        
        if (!settings) throw new Error('Portfolio settings not found');
        
        const cost = position.entry_price * position.shares;
        
        if (cost > settings.current_cash) {
          throw new Error('Insufficient funds');
        }
        
        // Create position
        const { data: newPosition, error: positionError } = await supabase
          .from('positions')
          .insert({
            ...position,
            user_id: user.id,
            is_active: true
          })
          .select()
          .single();
          
        if (positionError) throw positionError;
        
        // Update cash balance
        const { error: settingsError } = await supabase
          .from('portfolio_settings')
          .update({ current_cash: settings.current_cash - cost })
          .eq('id', settings.id);
          
        if (settingsError) throw settingsError;
        
        return newPosition as PortfolioPosition;
      } catch (error) {
        console.error('Error creating position:', error);
        return null;
      }
    },
    
    // Close position (sell)
    closePosition: async (positionId: string, exitPrice: number, exitDate: string): Promise<PortfolioPosition | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get the position
        const { data: position, error: getError } = await supabase
          .from('positions')
          .select('*')
          .eq('id', positionId)
          .eq('user_id', user.id)
          .single();
          
        if (getError) throw getError;
        
        // Get settings
        const settings = await supabaseService.portfolio.getSettings();
        
        if (!settings) throw new Error('Portfolio settings not found');
        
        // Calculate proceeds
        const proceeds = exitPrice * position.shares;
        
        // Update position
        const { data: updatedPosition, error: updateError } = await supabase
          .from('positions')
          .update({
            exit_date: exitDate,
            exit_price: exitPrice,
            is_active: false
          })
          .eq('id', positionId)
          .select()
          .single();
          
        if (updateError) throw updateError;
        
        // Update cash balance
        const { error: settingsError } = await supabase
          .from('portfolio_settings')
          .update({ current_cash: settings.current_cash + proceeds })
          .eq('id', settings.id);
          
        if (settingsError) throw settingsError;
        
        return updatedPosition as PortfolioPosition;
      } catch (error) {
        console.error('Error closing position:', error);
        return null;
      }
    },
    
    // Take performance snapshot
    takeSnapshot: async (portfolioValue: number): Promise<PerformanceSnapshot | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return null;
        
        // Get settings
        const settings = await supabaseService.portfolio.getSettings();
        
        if (!settings) throw new Error('Portfolio settings not found');
        
        // Today's date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0];
        
        // Create snapshot
        const { data, error } = await supabase
          .from('performance_snapshots')
          .insert({
            user_id: user.id,
            date: today,
            portfolio_value: portfolioValue,
            cash_balance: settings.current_cash
          })
          .select()
          .single();
          
        if (error) throw error;
        
        return data as PerformanceSnapshot;
      } catch (error) {
        console.error('Error creating performance snapshot:', error);
        return null;
      }
    },
    
    // Get performance snapshots
    getSnapshots: async (limit: number = 30): Promise<PerformanceSnapshot[]> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) return [];
        
        const { data, error } = await supabase
          .from('performance_snapshots')
          .select('*')
          .eq('user_id', user.id)
          .order('date', { ascending: false })
          .limit(limit);
          
        if (error) throw error;
        
        return data as PerformanceSnapshot[];
      } catch (error) {
        console.error('Error getting performance snapshots:', error);
        return [];
      }
    },
  },
  
  // Companies and tickers
  companies: {
    getTickers: async () => {
      const { data, error } = await supabase
        .from('sp500_tickers')
        .select('ticker')
        .order('ticker');
      
      if (error) throw error;
      return data.map(item => item.ticker);
    },
    
    getCompanyDetails: async (ticker: string) => {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('ticker', ticker)
        .single();
      
      if (error) throw error;
      return data;
    },
  },
  
  // Price history
  priceHistory: {
    getForTicker: async (ticker: string) => {
      const { data, error } = await supabase
        .from('price_history')
        .select('*')
        .eq('ticker', ticker)
        .order('date', { ascending: false });
      
      if (error) throw error;
      return data as PricePoint[];
    },
  },
  
  // Trading signals
  tradingSignals: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('trading_signals')
        .select('*')
        .order('date', { ascending: false });
      
      if (error) throw error;
      return data as TradingSignal[];
    },
    
    getForTicker: async (ticker: string) => {
      const { data, error } = await supabase
        .from('trading_signals')
        .select('*')
        .eq('ticker', ticker)
        .order('date', { ascending: false })
        .limit(1);
      
      if (error) throw error;
      return data.length > 0 ? data[0] as TradingSignal : null;
    },
    
    saveSignal: async (signal: Omit<TradingSignal, 'id'>) => {
      const { data, error } = await supabase
        .from('trading_signals')
        .insert(signal)
        .select()
        .single();
        
      if (error) throw error;
      return data as TradingSignal;
    },
  },
  
  // Point & Figure charts
  pnfCharts: {
    getForTicker: async (ticker: string) => {
      const { data, error } = await supabase
        .from('pnf_charts')
        .select('*')
        .eq('ticker', ticker)
        .order('date', { ascending: false })
        .limit(1);
      
      if (error) throw error;
      return data.length > 0 ? data[0] as PnFChart : null;
    },
  },
  
  // CAPE scores
  capeScores: {
    getForTicker: async (ticker: string) => {
      const { data, error } = await supabase
        .from('cape_scores')
        .select('*')
        .eq('ticker', ticker)
        .order('date', { ascending: false })
        .limit(1);
      
      if (error) throw error;
      return data.length > 0 ? data[0] as CapeScore : null;
    },
  },
  
  // F-scores
  fScores: {
    getForTicker: async (ticker: string) => {
      const { data, error } = await supabase
        .from('f_scores')
        .select('*')
        .eq('ticker', ticker)
        .order('date', { ascending: false })
        .limit(1);
      
      if (error) throw error;
      return data.length > 0 ? data[0] as FScore : null;
    },
  },
  
  // Database schema setup (run once when app initializes)
  setupDatabase: async (): Promise<boolean> => {
    try {
      // First check if required tables exist
      const { data: existingTables, error: tablesError } = await supabase
        .from('information_schema.tables')
        .select('table_name')
        .eq('table_schema', 'public');
        
      if (tablesError) {
        console.error('Error checking tables:', tablesError);
        return false;
      }

      const requiredTables = [
        'profiles',
        'portfolio_settings',
        'positions',
        'performance_snapshots',
        'sp500_tickers',
        'trading_signals',
        'financial_data_cache'
      ];
      
      // Check which tables exist
      const existingTableNames = existingTables.map(t => t.table_name);
      const missingTables = requiredTables.filter(t => !existingTableNames.includes(t));
      
      if (missingTables.length === 0) {
        console.log('All required database tables exist');
        return true;
      }
      
      console.log('Missing tables:', missingTables);
      
      // Create missing tables
      for (const table of missingTables) {
        await createTable(table);
      }
      
      return true;
    } catch (error) {
      console.error('Error setting up database:', error);
      return false;
    }
  }
};

// Helper function to create a specific table
async function createTable(tableName: string): Promise<void> {
  let query = '';
  
  switch (tableName) {
    case 'profiles':
      query = `
        CREATE TABLE profiles (
          id UUID PRIMARY KEY REFERENCES auth.users(id),
          email TEXT NOT NULL,
          display_name TEXT,
          avatar_url TEXT,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
      `;
      break;
      
    case 'portfolio_settings':
      query = `
        CREATE TABLE portfolio_settings (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          user_id UUID REFERENCES profiles(id) NOT NULL,
          initial_cash DECIMAL(15, 2) NOT NULL DEFAULT 100000.00,
          current_cash DECIMAL(15, 2) NOT NULL DEFAULT 100000.00,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          UNIQUE(user_id)
        );
      `;
      break;
      
    case 'positions':
      query = `
        CREATE TABLE positions (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          user_id UUID REFERENCES profiles(id) NOT NULL,
          ticker TEXT NOT NULL,
          entry_date DATE NOT NULL,
          entry_price DECIMAL(15, 2) NOT NULL,
          shares DECIMAL(15, 2) NOT NULL,
          exit_date DATE,
          exit_price DECIMAL(15, 2),
          is_active BOOLEAN DEFAULT TRUE,
          notes TEXT,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
      `;
      break;
      
    case 'performance_snapshots':
      query = `
        CREATE TABLE performance_snapshots (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          user_id UUID REFERENCES profiles(id) NOT NULL,
          date DATE NOT NULL,
          portfolio_value DECIMAL(15, 2) NOT NULL,
          cash_balance DECIMAL(15, 2) NOT NULL,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          UNIQUE(user_id, date)
        );
      `;
      break;
      
    case 'sp500_tickers':
      query = `
        CREATE TABLE sp500_tickers (
          ticker TEXT PRIMARY KEY,
          name TEXT,
          sector TEXT,
          weight DECIMAL(8, 5),
          added_date DATE DEFAULT CURRENT_DATE
        );
      `;
      break;
      
    case 'trading_signals':
      query = `
        CREATE TABLE trading_signals (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          ticker TEXT NOT NULL,
          date DATE NOT NULL,
          cape DECIMAL(15, 5),
          cape_percentile DECIMAL(6, 2),
          f_score INTEGER,
          preliminary_signal TEXT,
          final_signal TEXT,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
      `;
      break;
      
    case 'financial_data_cache':
      query = `
        CREATE TABLE financial_data_cache (
          key TEXT PRIMARY KEY,
          ticker TEXT,
          function_name TEXT,
          data JSONB NOT NULL,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          expires_at TIMESTAMP WITH TIME ZONE
        );
      `;
      break;
      
    default:
      console.error(`No schema definition for table: ${tableName}`);
      return;
  }
  
  try {
    await supabase.rpc('execute_sql', { query });
    console.log(`Created table: ${tableName}`);
    
    // Set up Row Level Security (RLS) policies
    await setupRLSForTable(tableName);
  } catch (error) {
    console.error(`Error creating table ${tableName}:`, error);
    throw error;
  }
}

// Helper function to set up RLS policies for a table
async function setupRLSForTable(tableName: string): Promise<void> {
  let queries: string[] = [];
  
  switch (tableName) {
    case 'profiles':
      queries = [
        `ALTER TABLE profiles ENABLE ROW LEVEL SECURITY;`,
        `CREATE POLICY "Users can view their own profile" ON profiles FOR SELECT USING (auth.uid() = id);`,
        `CREATE POLICY "Users can update their own profile" ON profiles FOR UPDATE USING (auth.uid() = id);`
      ];
      break;
      
    case 'portfolio_settings':
      queries = [
        `ALTER TABLE portfolio_settings ENABLE ROW LEVEL SECURITY;`,
        `CREATE POLICY "Users can view their own portfolio settings" ON portfolio_settings FOR SELECT USING (auth.uid() = user_id);`,
        `CREATE POLICY "Users can update their own portfolio settings" ON portfolio_settings FOR UPDATE USING (auth.uid() = user_id);`,
        `CREATE POLICY "Users can insert their own portfolio settings" ON portfolio_settings FOR INSERT WITH CHECK (auth.uid() = user_id);`
      ];
      break;
      
    case 'positions':
      queries = [
        `ALTER TABLE positions ENABLE ROW LEVEL SECURITY;`,
        `CREATE POLICY "Users can view their own positions" ON positions FOR SELECT USING (auth.uid() = user_id);`,
        `CREATE POLICY "Users can insert their own positions" ON positions FOR INSERT WITH CHECK (auth.uid() = user_id);`,
        `CREATE POLICY "Users can update their own positions" ON positions FOR UPDATE USING (auth.uid() = user_id);`,
        `CREATE POLICY "Users can delete their own positions" ON positions FOR DELETE USING (auth.uid() = user_id);`
      ];
      break;
      
    case 'performance_snapshots':
      queries = [
        `ALTER TABLE performance_snapshots ENABLE ROW LEVEL SECURITY;`,
        `CREATE POLICY "Users can view their own performance snapshots" ON performance_snapshots FOR SELECT USING (auth.uid() = user_id);`,
        `CREATE POLICY "Users can insert their own performance snapshots" ON performance_snapshots FOR INSERT WITH CHECK (auth.uid() = user_id);`
      ];
      break;
      
    // These tables are public - all users can read them but only the admin can modify
    case 'sp500_tickers':
    case 'trading_signals':
    case 'financial_data_cache':
      queries = [
        `ALTER TABLE ${tableName} ENABLE ROW LEVEL SECURITY;`,
        `CREATE POLICY "Public read access" ON ${tableName} FOR SELECT USING (true);`
      ];
      break;
      
    default:
      console.warn(`No RLS policies defined for table: ${tableName}`);
      return;
  }
  
  // Apply RLS policies
  for (const query of queries) {
    try {
      await supabase.rpc('execute_sql', { query });
    } catch (error) {
      console.error(`Error applying RLS policy to ${tableName}:`, error);
    }
  }
  
  console.log(`Applied RLS policies to table: ${tableName}`);
}

// Get the current user
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Error getting user:', error);
    return null;
  }
  return data.user;
};

// Get current session
export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error getting session:', error);
    return null;
  }
  return data.session;
};

// Sign in with email and password
export const signInWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
};

// Sign up with email and password
export const signUpWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });
  if (error) throw error;
  return data;
};

// Sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

// Reset password
export const resetPassword = async (email: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
};

// Update user
export const updateUser = async (attributes: { [key: string]: any }) => {
  const { data, error } = await supabase.auth.updateUser(attributes);
  if (error) throw error;
  return data;
};

// Check if we should use mock data instead of real data
export const useMockSupabase = !supabaseUrl || !supabaseAnonKey || config.useMockApi;

// Export utility to determine if we should use mock data
export const shouldUseMockData = () => useMockSupabase;

export default {
  supabase,
  supabaseService,
  getCurrentUser,
  getSession,
  signInWithEmail,
  signUpWithEmail,
  signOut,
  resetPassword,
  updateUser,
  useMockSupabase,
  shouldUseMockData
};
